import {
  type UniversalHelpClientOptions as ClientOpts,
  UniversalHelpClient,
} from '@stripe-internal/universal-help-conversations-client';
import {
  type ClientResponse,
  type Conversation,
  EmailIssueClientUrls,
  type Observability,
  RequestACallClientUrls,
  type RequestAction,
} from '@stripe-internal/conversations-client';
import {InlineDataEventBus} from '@stripe-internal/inline-data';
import {TwilioClient} from '@stripe-internal/twilio-conversations-client';

import {Api} from '../../core/lib/api/api';
import {AppConfig, AppProfile} from '../../core/types';
import {
  type AssistantClient,
  SupportConversationsClient,
} from '../../core/lib/SupportConversationsClient';
import {isDotComEntryPoint} from '../../core/lib/util/conversationUtils';
import {syncStorageModeForThreadsChannel} from './storage';

export function buildV1Clients({
  getConfig,
  getProfile,
  getApi,
  getObservability,
  inlineDataEventBus,
  requestAction,
  getCurrentConversation,
  withHistory,
}: {
  getConfig: () => AppConfig;
  getProfile: () => AppProfile;
  getApi: () => Api;
  getObservability: () => Observability;
  inlineDataEventBus: InlineDataEventBus;
  requestAction: (action: RequestAction) => Promise<ClientResponse>;
  getCurrentConversation?: () => Conversation | null | undefined;
  withHistory: boolean;
}) {
  const {
    audience,
    entryPoint,
    urls: {attachmentDownloadPath},
  } = getConfig();
  const {merchant, featureFlags} = getProfile();

  const emailUrls: EmailIssueClientUrls = {
    emailIssueUrl: '/api/contact-form',
  };

  const racUrls: RequestACallClientUrls = {
    requestACallUrl: '/api/request-a-call',
    verificationCodesUrl: `/api/request-a-call/verification-code?redirectUrl=${document.referrer}`,
    channelsUrl: `/api/eligible-channels/${entryPoint}`,
  };

  const uhUrls = {
    conversationUrl: '/api/universal-help',
    attachmentDownloadPath,
    ...emailUrls,
    ...racUrls,
  };

  const humanAgentGroup = isDotComEntryPoint(entryPoint) ? 'sales' : 'support';

  const deviceToken = featureFlags.enableLiveChatSupportInMobile
    ? new URLSearchParams(window.location.search).get('device_token')
    : null;
  const deviceTokenParam = deviceToken
    ? `&device_token=${encodeURIComponent(deviceToken)}`
    : '';

  const chatUrls = {
    tokenUrl: `/api/chat-token?audience=${audience}&entrypoint=${entryPoint}`,
    conversationUrl: `/api/start-chat?audience=${audience}&entrypoint=${entryPoint}&human_agent_group=${humanAgentGroup}${deviceTokenParam}`,
    ...emailUrls,
    ...racUrls,
  };

  const baseClientOpts: ClientOpts = {
    clientFetch: getApi().fetch,
    observability: getObservability(),
  };

  const chatClientOpts: ClientOpts = {
    ...baseClientOpts,
    urls: chatUrls,
  };

  const uhClientOpts: ClientOpts = {
    ...baseClientOpts,
    urls: uhUrls,
    userCountry: merchant?.country,
    enableUhPendingInput: featureFlags.enableUhPendingInput,
    enableConversationsThreads: featureFlags.enableConversationsThreads,
  };

  const uhClient: AssistantClient = new UniversalHelpClient(uhClientOpts);
  const chatClient = new TwilioClient(chatClientOpts);

  const client = new SupportConversationsClient({
    getConfig,
    getProfile,
    getApi,
    getObservability,
    uhClient,
    chatClient,
    inlineDataEventBus,
    requestAction,
    getCurrentConversation,
    enableCrossTabChatTransitionSync: !withHistory,
    getSyncStorageModeForChannel: (channel, syncStorageMode) =>
      syncStorageModeForThreadsChannel(channel, syncStorageMode, withHistory),
  });

  return {
    client,
    chatClient,
    uhClient,
  };
}
